/**
 * Exact money for billing terms (ADR-0073).
 *
 * Every amount is an integer number of US cents. There are no floats anywhere in the model, no
 * currency other than USD, and no rounding except in one place: applying a percentage discount,
 * which rounds half-up to the cent and is tested at the boundary. A terms version, an invoice, and
 * a Stripe request all carry the same integers, so a total shown to an employee is the total sent
 * to Stripe.
 */

export const BILLING_CURRENCY = "usd";

/** Ten million dollars, in cents. Larger than any negotiated term and small enough to be a typo guard. */
export const MAX_AMOUNT_MINOR = 1_000_000_000;

/** Basis points: a percentage discount is an integer 1..10000, so 12.5 % is 1250 and never 0.125. */
export const MAX_DISCOUNT_BASIS_POINTS = 10_000;

export const MAX_LINE_QUANTITY = 10_000;

export type BillingDiscount =
  | { type: "percent"; basisPoints: number }
  | { type: "fixed"; amountMinor: number };

export type BillingCadence = "month" | "year";

export type BillingLineKind = "one_time" | "recurring" | "credit";

export type BillingLineInput = {
  kind: BillingLineKind;
  unitAmountMinor: number;
  quantity: number;
  cadence: BillingCadence | null;
  discount: BillingDiscount | null;
};

export type BillingTotals = {
  /** Charged once, after line and terms-level discounts. */
  oneTimeMinor: number;
  /** Charged every month, after discounts. */
  monthlyMinor: number;
  /** Charged every year, after discounts. */
  annualMinor: number;
  /** Credit the customer draws down against future charges. Never discounted. */
  creditMinor: number;
};

export function isMinorAmount(value: unknown): value is number {
  return (
    typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= MAX_AMOUNT_MINOR
  );
}

/**
 * Half-up to the cent, in integer arithmetic. `amount * basisPoints` stays below 2^53 for every
 * amount the schema admits, so this never passes through a float.
 */
export function percentOf(amountMinor: number, basisPoints: number): number {
  return Math.floor(
    (amountMinor * basisPoints + MAX_DISCOUNT_BASIS_POINTS / 2) / MAX_DISCOUNT_BASIS_POINTS,
  );
}

/** A discount can only reduce an amount, never below zero. */
export function applyDiscount(amountMinor: number, discount: BillingDiscount | null): number {
  if (discount === null) return amountMinor;
  const reduction =
    discount.type === "percent"
      ? percentOf(amountMinor, discount.basisPoints)
      : discount.amountMinor;
  return Math.max(0, amountMinor - reduction);
}

/** Unit price times quantity, less this line's own discount. */
export function lineTotalMinor(line: BillingLineInput): number {
  return applyDiscount(line.unitAmountMinor * line.quantity, line.discount);
}

/**
 * The four numbers a terms version amounts to.
 *
 * A terms-level percentage applies to every charged bucket. A terms-level fixed amount comes off
 * the one-time bucket only: taking a fixed sum off "every month forever" is not what anybody
 * negotiating a fixed discount means, and the schema refuses a fixed terms discount larger than the
 * one-time charges it can come off.
 */
export function billingTotals(
  lines: readonly BillingLineInput[],
  termsDiscount: BillingDiscount | null,
): BillingTotals {
  let oneTime = 0;
  let monthly = 0;
  let annual = 0;
  let credit = 0;
  for (const line of lines) {
    const total = lineTotalMinor(line);
    if (line.kind === "credit") credit += total;
    else if (line.kind === "one_time") oneTime += total;
    else if (line.cadence === "year") annual += total;
    else monthly += total;
  }
  if (termsDiscount?.type === "percent") {
    return {
      oneTimeMinor: applyDiscount(oneTime, termsDiscount),
      monthlyMinor: applyDiscount(monthly, termsDiscount),
      annualMinor: applyDiscount(annual, termsDiscount),
      creditMinor: credit,
    };
  }
  return {
    oneTimeMinor: applyDiscount(oneTime, termsDiscount),
    monthlyMinor: monthly,
    annualMinor: annual,
    creditMinor: credit,
  };
}

/** Whether a fixed terms-level discount has one-time charges to come off. */
export function fixedTermsDiscountFits(
  lines: readonly BillingLineInput[],
  termsDiscount: BillingDiscount | null,
): boolean {
  if (termsDiscount?.type !== "fixed") return true;
  const oneTime = lines
    .filter((line) => line.kind === "one_time")
    .reduce((sum, line) => sum + lineTotalMinor(line), 0);
  return termsDiscount.amountMinor <= oneTime;
}

/** `123456` → `$1,234.56`. Cents are always shown, so `$12.00` and `$12.50` line up. */
export function formatUsd(amountMinor: number): string {
  const sign = amountMinor < 0 ? "-" : "";
  const absolute = Math.abs(amountMinor);
  const dollars = Math.floor(absolute / 100);
  const cents = absolute % 100;
  const grouped = dollars.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${sign}$${grouped}.${cents.toString().padStart(2, "0")}`;
}

/** "$5,000.00 once + $250.00 per month" for a card or a mail, or "Nothing charged" for an empty one. */
export function summarizeTotals(totals: BillingTotals): string {
  const parts = [
    totals.oneTimeMinor > 0 ? `${formatUsd(totals.oneTimeMinor)} once` : null,
    totals.monthlyMinor > 0 ? `${formatUsd(totals.monthlyMinor)} per month` : null,
    totals.annualMinor > 0 ? `${formatUsd(totals.annualMinor)} per year` : null,
    totals.creditMinor > 0 ? `${formatUsd(totals.creditMinor)} credit` : null,
  ].filter((part): part is string => part !== null);
  return parts.length === 0 ? "Nothing charged" : parts.join(" + ");
}

/**
 * Parse a typed dollar amount such as `1,250` or `1250.5` into cents, or `null` when it is not an
 * exact amount. Refuses more than two decimals rather than rounding: an employee who typed a
 * third decimal made a mistake, and a form that quietly rounds it is how the wrong total ships.
 */
export function parseUsd(input: string): number | null {
  const cleaned = input.trim().replace(/^\$/, "").replace(/,/g, "");
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(cleaned);
  if (!match?.[1]) return null;
  const dollars = Number(match[1]);
  const cents = Number((match[2] ?? "").padEnd(2, "0"));
  const minor = dollars * 100 + cents;
  return isMinorAmount(minor) ? minor : null;
}
